import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { Toggle } from 'components/Toggle';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { useStore } from 'context/store-context';
import { GET_RETURN_EXCHANGE_INCENTIVE_SETTINGS } from 'gql/queries';
import { SAVE_RETURN_EXCHANGE_INCENTIVE_SETTINGS } from 'gql/mutations';
import ExchangeIncentiveRuleEditor from './ExchangeIncentiveRuleEditor';
import ExchangeBonusRuleEditor, {newExchangeBonusRule} from './ExchangeBonusRuleEditor';
import {
  ExchangeIncentiveRecord as RecordValue,
  ExchangeIncentiveDraft as Draft,
  parseExchangeIncentiveRecord as parse,
  exchangeIncentiveDraftOf as draftOf,
  exchangeIncentiveSettingsOf as settingsOf,
  sameExchangeIncentives as same,
  exchangeCreditCurrencies,
} from './ExchangeIncentiveSettings.policy';
export type ExchangeIncentiveSaveState = {
  available: boolean;
  dirty: boolean;
  saving: boolean;
  save: () => Promise<boolean>;
  refresh: () => Promise<boolean>;
};
const bonusError =
  'Enter a bonus from 0 to 100 with no more than two decimal places.';
const reloadError =
  'Exchange incentives could not be verified. Reload saved exchange incentives before trying again.';

export default function ExchangeIncentiveSettings({
  onSaveStateChange,
  disabled = false,
}: {
  onSaveStateChange: (value: ExchangeIncentiveSaveState) => void;
  disabled?: boolean;
}) {
  const { storeId: selectedStoreId, storeProperties } = useStore();
  const storeId = selectedStoreId || '';
  const owner = useRef({ storeId });
  if (owner.current.storeId !== storeId) owner.current = { storeId };
  const scope = owner.current;
  const mounted = useRef(true),
    pending = useRef<{ scope: object } | null>(null);
  if (pending.current && pending.current.scope !== scope)
    pending.current = null;
  type State = {
    scope: object;
    source: RecordValue | null;
    draft: Draft | null;
    saving: boolean;
    error: string;
    reloadRequired: boolean;
    saved: boolean;
  };
  const [state, setState] = useState<State>({
    scope,
    source: null,
    draft: null,
    saving: false,
    error: '',
    reloadRequired: false,
    saved: false,
  });
  const current =
    state.scope === scope
      ? state
      : {
          scope,
          source: null,
          draft: null,
          saving: false,
          error: '',
          reloadRequired: false,
          saved: false,
        };
  const latest = useRef(current);
  latest.current = current;
  const read = useQuery(GET_RETURN_EXCHANGE_INCENTIVE_SETTINGS, {
    variables: { input: { storeId } },
    skip: !storeId,
    fetchPolicy: 'network-only',
  });
  const [write] = useMutation(SAVE_RETURN_EXCHANGE_INCENTIVE_SETTINGS);
  const raw = read.data?.returnExchangeIncentiveSettings;
  const live = () => mounted.current && owner.current === scope;
  const update = (next: State) => {
    if (live()) {
      latest.current = next;
      setState(next);
    }
  };
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    const record = storeId ? parse(raw, storeId) : null;
    if (
      record &&
      !read.loading &&
      !read.error &&
      !latest.current.source &&
      pending.current === null
    )
      update({
        scope,
        source: record,
        draft: draftOf(record.settings),
        saving: false,
        error: '',
        reloadRequired: false,
        saved: false,
      });
    // Same-store cache reads cannot replace drafts or an acknowledged save. Reload is explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, raw, read.loading, read.error]);
  const selectedSettings = current.draft ? settingsOf(current.draft) : null;
  const dirty =
    !!current.source &&
    !!current.draft &&
    (current.source.legacy || !selectedSettings || !same(selectedSettings, current.source.settings));
  const available = !!current.source;
  const lock = current.saving || disabled || current.reloadRequired;
  function change(draft: Draft) {
    if (
      !live() ||
      latest.current !== current ||
      pending.current ||
      disabled ||
      !latest.current.source ||
      latest.current.reloadRequired
    )
      return;
    update({ ...latest.current, draft, error: '', saved: false });
  }
  async function save(): Promise<boolean> {
    const before = latest.current;
    if (
      !live() ||
      pending.current ||
      !before.source ||
      !before.draft ||
      before.reloadRequired
    )
      return false;
    const settings = settingsOf(before.draft);
    if (!settings) {
      const bonusValid = settingsOf({ ...before.draft, keepItemCredit: { enabled: false, rules: [] } }) !== null;
      update({ ...before, error: 'Check credit rule values, currency, eligibility and offer limits.' + (bonusValid ? '' : ' ' + (Object.hasOwnProperty.call(before.draft.cheaperDifferenceStoreCredit, 'bonusRules') ? 'Check bonus rule values, currency, eligibility and rule limits.' : bonusError)) });
      return false;
    }
    if (!before.source.legacy && same(settings, before.source.settings)) return true;
    const token = { scope };
    pending.current = token;
    const owns = () => live() && pending.current === token;
    update({ ...before, saving: true, error: '', saved: false });
    try {
      const result = await write({
        variables: {
          input: {
            storeId,
            expectedRevision: before.source.revision,
            settings,
          },
        },
      });
      if (!owns()) return false;
      const record = parse(
        result.data?.saveReturnExchangeIncentiveSettings,
        storeId,
      );
      if (
        result.errors?.length ||
        !record || record.legacy ||
        record.revision === before.source.revision ||
        !same(record.settings, settings)
      )
        throw new Error('Unverified exchange incentive acknowledgement');
      update({
        scope,
        source: record,
        draft: draftOf(record.settings),
        saving: false,
        error: '',
        reloadRequired: false,
        saved: true,
      });
      return true;
    } catch {
      if (owns())
        update({
          ...latest.current,
          saving: false,
          error: reloadError,
          reloadRequired: true,
        });
      return false;
    } finally {
      if (owns()) pending.current = null;
    }
  }
  async function reload(preserveClean = false): Promise<boolean> {
    if (!live() || pending.current) return false;
    const before = latest.current;
    const draftSettings = before.draft ? settingsOf(before.draft) : null;
    if (
      preserveClean &&
      before.source &&
      (!draftSettings || !same(draftSettings, before.source.settings))
    )
      return false;
    const token = { scope };
    pending.current = token;
    const owns = () => live() && pending.current === token;
    update({ ...before, saving: true, error: '' });
    try {
      const result = await read.refetch({ input: { storeId } });
      if (!owns()) return false;
      const record = parse(
        result.data?.returnExchangeIncentiveSettings,
        storeId,
      );
      if (
        result.errors?.length ||
        result.error ||
        !record ||
        (before.source &&
          record.revision === before.source.revision &&
          !same(record.settings, before.source.settings))
      )
        throw new Error('Unverified exchange incentive read');
      update({
        scope,
        source: record,
        draft: draftOf(record.settings),
        saving: false,
        error: '',
        reloadRequired: false,
        saved: false,
      });
      return true;
    } catch {
      if (owns())
        update({
          ...latest.current,
          saving: false,
          error: reloadError,
          reloadRequired: true,
        });
      return false;
    } finally {
      if (owns()) pending.current = null;
    }
  }
  const handlers = useRef({ save, reload });
  handlers.current = { save, reload };
  const saveCurrent = useCallback(
    () =>
      mounted.current && owner.current === scope
        ? handlers.current.save()
        : Promise.resolve(false),
    [scope],
  );
  const refreshCurrent = useCallback(
    () =>
      mounted.current && owner.current === scope
        ? handlers.current.reload(true)
        : Promise.resolve(false),
    [scope],
  );
  const report = useRef(onSaveStateChange);
  report.current = onSaveStateChange;
  useEffect(() => {
    report.current({
      available,
      dirty,
      saving: current.saving,
      save: saveCurrent,
      refresh: refreshCurrent,
    });
  }, [available, dirty, current.saving, saveCurrent, refreshCurrent, scope]);
  const difference = current.draft?.cheaperDifferenceStoreCredit;
  const currency = storeProperties?.id === storeId ? storeProperties.currency : undefined;
  const rulesMode = !!difference && Object.hasOwnProperty.call(difference, 'bonusRules');
  const legacyBonus = current.draft && !rulesMode ? settingsOf({...current.draft, keepItemCredit:{enabled:false,rules:[]}})?.cheaperDifferenceStoreCredit : null;
  return (
    <section
      aria-labelledby="exchange-incentives-heading"
      className="space-y-4 rounded-xl border border-gray-200 bg-white p-5"
    >
      <div>
        <h2
          id="exchange-incentives-heading"
          className="text-base font-semibold text-gray-900"
        >
          Exchange incentives
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose optional offers for eligible exchanges. Shared eligibility,
          customer restrictions and required checks still apply.
        </p>
      </div>
      {current.error && (
        <div
          role="alert"
          className="space-y-2 rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          <p>{current.error}</p>
          {current.reloadRequired && (
            <Button
              variant="outlined"
              disabled={current.saving || disabled}
              onClick={() => reload()}
            >
              Reload saved exchange incentives
            </Button>
          )}
        </div>
      )}
      {!current.source || !current.draft ? (
        <div>
          <p role={read.loading ? 'status' : 'alert'}>
            {read.loading
              ? 'Loading exchange incentives…'
              : 'Exchange incentives are unavailable.'}
          </p>
          {!read.loading && !current.reloadRequired && (
            <Button
              variant="outlined"
              disabled={current.saving || disabled || !storeId}
              onClick={() => reload()}
            >
              Reload saved exchange incentives
            </Button>
          )}
        </div>
      ) : (
        <>
          {current.saved && (
            <p role="status" className="text-sm text-green-700">
              Exchange incentives saved.
            </p>
          )}
          {current.source.legacy && <p role="note" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">Legacy settings include keep-original offers with a replacement and credit alternatives that require a return. Saving these new settings replaces future offers only; existing requests keep their accepted terms. The new keep-item credit rules start off.</p>}
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Cheaper exchange differences</h3>
            <Toggle
              ariaLabel="Offer store credit for cheaper exchange differences"
              rightLabel="Offer store credit for cheaper exchange differences"
              isEnabled={difference!.enabled}
              disabled={lock}
              onChange={enabled => change({ ...current.draft!, cheaperDifferenceStoreCredit: { ...difference!, enabled } })}
            />
            <p className="text-xs leading-5 text-gray-500">Let customers choose store credit instead of a cash refund for the eligible price difference.</p>
            <div className="space-y-3 border-t border-gray-100 pt-3">
              <Toggle ariaLabel="Add a bonus to cheaper exchange credit" rightLabel="Add a bonus to cheaper exchange credit" isEnabled={difference!.bonusEnabled} disabled={lock || !difference!.enabled} onChange={bonusEnabled => { if (difference!.enabled) change({ ...current.draft!, cheaperDifferenceStoreCredit: { ...difference!, bonusEnabled } }); }} />
              {rulesMode ? <ExchangeBonusRuleEditor rules={difference!.bonusRules!} storeId={storeId} currency={currency} disabled={lock || !difference!.enabled || !difference!.bonusEnabled} onChange={bonusRules=>{if(difference!.enabled&&difference!.bonusEnabled)change({...current.draft!,cheaperDifferenceStoreCredit:{...difference!,bonusRules}});}}/> : <>
              <label className="block max-w-sm text-sm font-medium text-gray-800">Bonus on the cheaper exchange difference (%)
                <NonNegativeNumberInput min="0" max="100" step="0.01" aria-label="Bonus on the cheaper exchange difference (%)" disabled={lock || !difference!.enabled || !difference!.bonusEnabled} value={difference!.bonusPercentage} onChange={event => { if (difference!.enabled && difference!.bonusEnabled) change({ ...current.draft!, cheaperDifferenceStoreCredit: { ...difference!, bonusPercentage: event.target.value } }); }} className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50" />
              </label>
              <p className="text-xs text-gray-500">The configured percentage is retained when the bonus is off.</p>
              <p className="text-xs text-gray-500">This percentage applies to all eligible cheaper exchanges when the bonus is on. Use rules to target original items, values and orders with SHIPAID Delivery Guarantee.</p>
              <p className="text-xs text-gray-500">{currency && exchangeCreditCurrencies.includes(currency) ? `Bonus rules use an explicit currency. New rules start in ${currency}; add rules for other currencies as needed.` : 'Load a supported store currency before creating bonus rules.'}</p>
              <Button variant="outlined" disabled={lock || !difference!.enabled || !difference!.bonusEnabled || !legacyBonus || !currency || !exchangeCreditCurrencies.includes(currency)} onClick={()=>{
                if(lock||!difference!.enabled||!difference!.bonusEnabled||!legacyBonus||!currency||!exchangeCreditCurrencies.includes(currency))return;
                change({...current.draft!,cheaperDifferenceStoreCredit:{...difference!,bonusPercentage:'0',bonusRules:legacyBonus.bonusPercentage>0?[{...newExchangeBonusRule(currency,legacyBonus.bonusPercentage),name:'Existing global bonus'}]:[]}});
              }}>Use bonus rules</Button>
              </>}
            </div>
          </div>
          <div className="space-y-3 rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">Keep the item, choose store credit</h3>
            <Toggle ariaLabel="Offer keep-item store credit" rightLabel="Offer keep-item store credit" isEnabled={current.draft.keepItemCredit.enabled} disabled={lock} onChange={enabled => change({ ...current.draft!, keepItemCredit: { ...current.draft!.keepItemCredit, enabled } })} />
            <p className="text-xs leading-5 text-gray-500">Customers keep the original item and choose store credit, with no replacement and no return shipment. Your rules set the credit amount; approval and credit checks still apply.</p>
            {current.draft.keepItemCredit.enabled && <ExchangeIncentiveRuleEditor rules={current.draft.keepItemCredit.rules} storeId={storeId} currency={storeProperties?.id === storeId ? storeProperties.currency : undefined} disabled={lock} onChange={rules => { if (current.draft!.keepItemCredit.enabled) change({ ...current.draft!, keepItemCredit: { ...current.draft!.keepItemCredit, rules } }); }} />}
          </div>
        </>
      )}
    </section>
  );
}
